import { styled } from '@/src/stitches.config'
import { GradientText } from '@/src/common/components/marketing/heros/StyledComponents'
import { StyledH1 } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import Image from 'next/image'
import LinkButton from '@/src/common/components/marketing/buttons/LinkButton'
import useWindowSize from '@/src/common/hooks/useWindowSize'

const ContentWrapper = styled('div', {
    position: 'relative',
    width: 390,
    maxWidth: '100%',
    display: 'flex',
    fd: 'column-reverse',
    ai: 'center',
    jc: 'space-between',
    gap: 20,
    my: 45,
    mx: 'auto',
    p: '20px 15px',
    br: 20,
    backgroundImage: `linear-gradient(19deg, rgb(244, 223, 255) 0%, rgb(223, 234, 255) 10%, rgb(237, 238, 239) 27%, rgb(255, 255, 255) 147%)`,
    variants: {
        variant: {
            'mobile-view-only': {},
            'mobile-view-small-font': {
                backgroundImage: `linear-gradient(19deg, rgb(244, 223, 255) 0%, rgb(223, 234, 255) 10%, rgb(237, 238, 239) 27%, white 43%)`,
            },
            standard: {
                '@media (min-width:556px)': {
                    width: '$default',
                    maxWidth: '$defaultMax',
                    fd: 'row',
                    py: '5vw',
                    pl: '4%',
                    minHeight: 325,
                },
                '@media (min-width: 1170px)': {
                    pl: 40,
                    py: 60,
                },
            },
            'image-left': {
                boxShadow: 'gray 0px 1px 7px -2px',
                backgroundImage: `linear-gradient(19deg, rgb(244, 223, 255) 0%, rgb(223, 234, 255) 10%, rgb(237, 238, 239) 27%, rgb(255, 255, 255) 43%)`,
                '@media (min-width:556px)': {
                    width: 1000,
                    fd: 'row-reverse',
                    jc: 'space-between',
                    py: 20,
                    pl: 0,
                },
            },
            'image-full-height': {
                '@media (min-width:556px)': {
                    width: '$default',
                    maxWidth: '$defaultMax',
                    fd: 'row',
                    p: 0,
                    pl: '4%',
                },
                '@media (min-width: 1170px)': {
                    pl: 40,
                },
            },
        },
    },
    defaultVariants: {
        variant: 'standard',
    },
})

const LeftContent = styled('div', {
    width: '100%',
    display: 'flex',
    fd: 'column',
    gap: 15,
    flex: 1.5,
    '& p': {
        mb: 0,
    },
    variants: {
        variant: {
            'mobile-view-only': {},
            'mobile-view-small-font': {},
            standard: {
                '@media (min-width:556px)': {
                    maxWidth: '55%',
                    br: 'unset',
                    '& h1, p': {
                        width: 455,
                        maxWidth: '100%',
                    },
                },
            },
            'image-left': {
                flex: 'unset',
                width: '100%',
                '@media (min-width: 556px)': {
                    width: '76%',
                    fd: 'row',
                    ai: 'center',
                    jc: 'space-between',
                    flexWrap: 'wrap',
                    '& h1, p': {
                        width: 375,
                        maxWidth: '100%',
                    },
                },
            },
            'image-full-height': {
                '@media (min-width:556px)': {
                    py: 40,
                    maxWidth: '55%',
                    br: 'unset',
                    '& h1, p': {
                        width: 455,
                        maxWidth: '100%',
                    },
                },
            },
        },
    },
    defaultVariants: {
        variant: 'standard',
    },
})
const RightContent = styled('div', {
    width: '100%',
    display: 'flex',
    fd: 'column',
    position: 'relative',
    flex: 1,
    img: {
        width: '100%',
        height: 'auto',
        maxHeight: 'auto',
        marginLeft: 'auto',
        br: 15,
    },
    variants: {
        variant: {
            'mobile-view-only': {},
            'mobile-view-small-font': {},
            standard: {
                '@media (min-width:556px)': {
                    position: 'relative',
                    maxWidth: 400,
                    transform: 'translateX(35px)',
                    img: {
                        transform: 'translateY(-50%)',
                        position: 'absolute',
                    },
                },
            },
            'image-left': {
                '@media (min-width:556px)': {
                    flex: 0.85,
                    '& img': {
                        width: '130%',
                        transform: 'translateX(-14%) translateY(-32%)',
                    },
                },
            },
            'image-full-height': {
                '& img': {
                    br: 0,
                },
                '@media (min-width:556px)': {
                    position: 'relative',
                    maxWidth: 400,
                    transform: 'translateX(19%)',
                    img: {
                        width: '93%',
                        transform: 'translateY(-50%)',
                        position: 'absolute',
                    },
                },
            },
        },
    },
    defaultVariants: {
        variant: 'standard',
    },
})

const H1ForHero = styled(StyledH1, {
    margin: 0,
    padding: 0,
    fontSize: '2rem',
    lineHeight: 1,
    '& span': {
        fontSize: '3.3rem',
    },
    variants: {
        type: {
            'mobile-view-only': {},
            standard: {
                '@media (min-width: 906px)': {
                    fontSize: '2.75rem',
                    '& span': {
                        fontSize: '3rem',
                    },
                },
            },
            'mobile-view-small-font': {
                fontSize: '1.25rem',
                '& span': {
                    fontSize: '2.35rem',
                },
            },
            'image-left': {
                '@media (min-width:500px)': {
                    '& span': {
                        fontSize: '2.45rem',
                    },
                },
            },
            'image-full-height': {
                textTransform: 'none',
                '& span': {
                    fontSize: '2rem',
                },
                '@media (min-width: 906px)': {
                    fontSize: '2.5rem',
                    '& span': {
                        fontSize: '2.5rem',
                    },
                },
            },
        },
    },
    defaultVariants: {
        type: 'standard',
    },
})

const StyledParagraph = styled('p', {
    lineHeight: 1.5,
})

const StyledLinkButton = styled(LinkButton, {
    height: 'fit-content',
    minWidth: 'fit-content',
})

export interface IHeroProps {
    bodyText: string
    gradientText: string
    headerText: string
    imageUrl: string
    buttonUrl?: string
    buttonLabel?: string
    variant?:
        | 'standard'
        | 'mobile-view-only'
        | 'mobile-view-small-font'
        | 'image-left'
        | 'image-full-height'
    style?: object
    showTermsAndConditions?: boolean
}

const TermsAndConditions = () => {
    return <span>Terms &#38; Conditions apply</span>
}

const ReferAFriendHero = ({
    bodyText,
    gradientText,
    headerText,
    imageUrl,
    buttonLabel = '',
    buttonUrl = '',
    variant = 'standard',
    style = {},
    showTermsAndConditions = true,
}: IHeroProps) => {
    const hasButton: boolean = !!buttonUrl && !!buttonLabel

    const { width: viewportWidth = 556 } = useWindowSize()
    const isMobileView =
        viewportWidth < 556 ||
        ['mobile-view-only', 'mobile-view-small-font'].includes(variant)

    return (
        <ContentWrapper variant={variant} style={style}>
            {isMobileView && showTermsAndConditions && <TermsAndConditions />}
            <LeftContent variant={variant}>
                <div>
                    <H1ForHero variant={'extra-bold-caps'} type={variant}>
                        {headerText} <br />
                        <GradientText>{gradientText}</GradientText>
                    </H1ForHero>
                    <StyledParagraph>
                        {bodyText}{' '}
                        {!isMobileView && showTermsAndConditions && (
                            <TermsAndConditions />
                        )}
                    </StyledParagraph>
                </div>
                {hasButton && (
                    <StyledLinkButton
                        label={buttonLabel}
                        url={buttonUrl}
                        variant={'primary'}
                        textTransform={'uppercase'}
                        size={'md'}
                        buttonWidth={isMobileView ? '100%' : undefined}
                        showArrow
                    />
                )}
            </LeftContent>
            <RightContent variant={variant}>
                <Image
                    src={imageUrl}
                    alt={'hero image'}
                    width={754}
                    height={771}
                />
            </RightContent>
        </ContentWrapper>
    )
}

export default ReferAFriendHero
